import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { ContentOrganizerStore } from '../stores';
import { IAToggle } from '../../toggle/Toggle';
import { LocalizationService } from '../../../services/LocalizationService';
import PermissionsService from '../../../services/PermissionsService';
import { IAccordion } from '../../../interfaces/IAccordion';
import { IAAccordion } from '../../accordion/Accordion';
import { IExtendedUserPermission, IUserPermission } from '../../../interfaces/IUserPermission';
import { IAPersonaSmall } from '../../personaSmall/PersonaSmall';
import { Helper } from '../../../Helper';
import { PermissionSwitch } from './../../permissions/permissionsPanel/permissionSwitch/PermissionSwitch';
import { PeopleSearch } from './peopleSearch/PeopleSearch'
import { IAButton } from '../../button/Button';
import { ISharePointUser } from '../../../interfaces/ISharePointUser';
import { IAIcon } from '../../icon/Icon';
import { IATooltip } from '../../tooltip/Tooltip';

const CONTENT_ORGANIZER_COMPONENT = "ContentOrganizer";

export interface IContentOrganizerPermissionsProps {
  style?: React.CSSProperties;
  contentOrganizerStore?: ContentOrganizerStore;
  beforeIaReady: () => void;
  afterIaReady: () => void;
}

export interface IContentOrganizerPermissionsState {
  color: string;
  enableTargeting: boolean;
}

export enum ContentOrganizerPermissionsInheritanceSource {
  Application = 1,
  Site = 2,
  Custom = 3
}

interface IContentOrganizerPermissionsCache {
  permissions: {[key: number]: IUserPermission[]}
}

@inject('contentOrganizerStore')
@observer
export class ContentOrganizerPermissions extends React.Component<IContentOrganizerPermissionsProps, IContentOrganizerPermissionsState> {
  private cache: IContentOrganizerPermissionsCache;
  private localizationService: LocalizationService = new LocalizationService();

  constructor(props: IContentOrganizerPermissionsProps, state: IContentOrganizerPermissionsState) {
    super(props, state);

    this.state = {
      color: "#025159",
      enableTargeting: false,
    }
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());

    this.cache = {
      permissions: {}
    }

    if (!this.props.contentOrganizerStore.newSiteCollection) {
      this.props.contentOrganizerStore.usePermissions = true;
      this.props.contentOrganizerStore.inheritSitePermissions = true;
      this.togglePermissionsFromSource(ContentOrganizerPermissionsInheritanceSource.Site, this.props.contentOrganizerStore.inheritSitePermissions);
    }
  }

  private getPermissionsTitleFromKey(role: "Administrator" | "Editor" | "Reader"): string {
    switch (role) {
      case "Administrator":
        return new LocalizationService().strings.Permissions_Owner;
      case "Editor":
        return new LocalizationService().strings.Permissions_Member;
      case "Reader":
        return new LocalizationService().strings.Permissions_Reader;
    }
  }

  private renderPermissions(): any {
    const administrators = [];
    const editors = [];
    const readers = [];
    if (this.props.contentOrganizerStore.permissions && this.props.contentOrganizerStore.permissions.length > 0) {
      this.props.contentOrganizerStore.permissions.forEach((userPermission: IExtendedUserPermission) => {
        switch (userPermission.role) {
          case "Administrator":
            administrators.push(
              <IAPersonaSmall
                profileImage={Helper.getProfileOrGroupImage(this.props.contentOrganizerStore.environment, this.props.contentOrganizerStore.tenant, this.props.contentOrganizerStore.token, userPermission.user.email, userPermission.user.userName)}
                name={userPermission.user.title}
                title={Helper.getProfileOrGroupType(this.localizationService, userPermission.user.userName)}
                metadata={
                  <PermissionSwitch
                    selectedOption={{
                      title: new LocalizationService().strings.Permissions_Owner,
                      key: "Administrator"
                    }}
                    selectOption={(selectedOption: "Administrator" | "Editor" | "Reader" | "Remove") => {
                      const index = this.props.contentOrganizerStore.permissions.findIndex(x => x.user.userName === userPermission.user.userName && x.role === userPermission.role);
                      if (selectedOption == "Remove") {
                        if (index > -1) {
                          this.props.contentOrganizerStore.permissions.splice(index, 1);
                        }
                      } else {
                        this.props.contentOrganizerStore.permissions[index].role = selectedOption;
                      }
                    }}
                  />
                }
                backgroundColor="#025159"
                styles={{
                  width: "100%",
                  marginTop: 11,
                  clear: "both"
                }
                }
                highlightColor={this.props.contentOrganizerStore.highlightColor}
              />
            );
            break;
          case "Editor":
            editors.push(
              <IAPersonaSmall
              profileImage={Helper.getProfileOrGroupImage(this.props.contentOrganizerStore.environment, this.props.contentOrganizerStore.tenant, this.props.contentOrganizerStore.token, userPermission.user.email, userPermission.user.userName)}
              name={userPermission.user.title}
              title={Helper.getProfileOrGroupType(this.localizationService, userPermission.user.userName)}
                metadata={
                  <PermissionSwitch
                    selectedOption={{
                      title: new LocalizationService().strings.Permissions_Member,
                      key: "Editor"
                    }}
                    selectOption={(selectedOption: "Administrator" | "Editor" | "Reader" | "Remove") => {
                      const index = this.props.contentOrganizerStore.permissions.findIndex(x => x.user.userName === userPermission.user.userName && x.role === userPermission.role);
                      if (selectedOption == "Remove") {
                        if (index > -1) {
                          this.props.contentOrganizerStore.permissions.splice(index, 1);
                        }
                      } else {
                        this.props.contentOrganizerStore.permissions[index].role = selectedOption;
                      }
                    }}
                  />
                }
                backgroundColor="#025159"
                styles={{
                  width: "100%",
                  marginTop: 11,
                  clear: "both"
                }}
                highlightColor={this.props.contentOrganizerStore.highlightColor}
              />
            );
            break;
          case "Reader":
            readers.push(
              <IAPersonaSmall
              profileImage={Helper.getProfileOrGroupImage(this.props.contentOrganizerStore.environment, this.props.contentOrganizerStore.tenant, this.props.contentOrganizerStore.token, userPermission.user.email, userPermission.user.userName)}
              name={userPermission.user.title}
              title={Helper.getProfileOrGroupType(this.localizationService, userPermission.user.userName)}
                metadata={
                  <PermissionSwitch
                    selectedOption={{
                      title: new LocalizationService().strings.Permissions_Reader,
                      key: "Reader"
                    }}
                    selectOption={(selectedOption: "Administrator" | "Editor" | "Reader" | "Remove") => {
                      const index = this.props.contentOrganizerStore.permissions.findIndex(x => x.user.userName === userPermission.user.userName && x.role === userPermission.role);
                      if (selectedOption == "Remove") {
                        if (index > -1) {
                          this.props.contentOrganizerStore.permissions.splice(index, 1);
                        }
                      } else {
                        this.props.contentOrganizerStore.permissions[index].role = selectedOption;
                      }
                    }}
                  />
                }
                backgroundColor="#025159"
                styles={{
                  width: "100%",
                  marginTop: 11,
                  clear: "both"
                }}
                highlightColor={this.props.contentOrganizerStore.highlightColor}
              />
            );
            break;
          default:
            break;
        }
      });
    }
    return {
      "Administrator": administrators,
      "Editor": editors,
      "Reader": readers,
    }
  }

  private togglePermissionsFromSource(source: ContentOrganizerPermissionsInheritanceSource, toggleState: boolean): void {
    if (toggleState) {
      if (!!this.cache.permissions[source]) {
        // Append to store from cache
        this.appendPermissionsToStore(this.cache.permissions[source], source)
      } else {
        // Append to store from API (and save to cache)
        switch (source) {
          case ContentOrganizerPermissionsInheritanceSource.Application:
            // Signal loading started
            this.props.beforeIaReady();
            // Load content organizer application (component) permissions
            PermissionsService.getPermission(this.props.contentOrganizerStore.environment, this.props.contentOrganizerStore.token, this.props.contentOrganizerStore.tenant, CONTENT_ORGANIZER_COMPONENT)
              .then((permissions: IUserPermission[]) => {
                // Store source permissions to cache
                this.cache.permissions[source] = permissions;
                // Append permissions to store
                this.appendPermissionsToStore(permissions, source);
                // Signal loading finished
                this.props.afterIaReady();
              })
              .catch(() => {
                // Signal loading finished
                this.props.afterIaReady();
                // Reject
                return Promise.reject();
              });
            break;
          case ContentOrganizerPermissionsInheritanceSource.Site:
            // Signal loading started
            this.props.beforeIaReady();
            // Load site permissions
            PermissionsService.getPermissionsForSite(this.props.contentOrganizerStore.environment, this.props.contentOrganizerStore.token, this.props.contentOrganizerStore.tenant, this.props.contentOrganizerStore.siteCollectionAddress)
              .then((permissions: IUserPermission[]) => {
                // Store source permissions to cache
                this.cache.permissions[source] = permissions;
                // Append permissions to store
                this.appendPermissionsToStore(permissions, source);
                // Signal loading finished
                this.props.afterIaReady();
              })
              .catch(() => {
                // Signal loading finished
                this.props.afterIaReady();
                // Even if it fails we need to wait for it to load - the site might just not exists
                return Promise.resolve();
              });
            break;
          default:
            // Permissions for given source cannot be loaded
            // Update source cache with a empty list
            this.cache.permissions[source] = [];
            break;
        }
      }
    } else {
      this.removeSourcePermissionsFromStore(source);
    }
  }

  private appendPermissionsToStore(permissions: IUserPermission[], source: ContentOrganizerPermissionsInheritanceSource): void {
    const extendedPermissions = permissions.map(p => {
      const ep = p as IExtendedUserPermission;
      ep.source = [source];
      return ep;
    })

    if (!this.props.contentOrganizerStore.permissions?.length) {
      this.props.contentOrganizerStore.permissions = extendedPermissions;
      return;
    }

    const permissionsToBeAppended = extendedPermissions
      .filter(x => !this.props.contentOrganizerStore.permissions.some(y => y.user.userName === x.user.userName && y.role === x.role));

    const permissionsToBeUpdated = extendedPermissions
      .filter(x => !permissionsToBeAppended.some(y => y.user.userName === x.user.userName && y.role === x.role));

    this.props.contentOrganizerStore.permissions.forEach((x, i) => {
      const updatedPermission = permissionsToBeUpdated.find(y => y.user.userName == x.user.userName && y.role == x.role);

      if (!updatedPermission) {
        return;
      }

      const sourcesToAppend = updatedPermission.source.filter(item => x.source.indexOf(item) < 0);

      this.props.contentOrganizerStore.permissions[i].source.push(...sourcesToAppend);
    });

    this.props.contentOrganizerStore.permissions.push(...permissionsToBeAppended);
  }

  private removeSourcePermissionsFromStore(source: ContentOrganizerPermissionsInheritanceSource): void {
    this.props.contentOrganizerStore.permissions.forEach((x, i) => {
      if (x.source.indexOf(source) > -1) {
        this.props.contentOrganizerStore.permissions[i].source = this.props.contentOrganizerStore.permissions[i].source.filter(s => s != source);
      }
    });

    this.props.contentOrganizerStore.permissions = this.props.contentOrganizerStore.permissions.filter(p => {
      return p.source.length > 0;
    })
  }

  public render(): JSX.Element {

    const permissions = this.renderPermissions();
    if (this.props.contentOrganizerStore.permissionItems) {
      this.props.contentOrganizerStore.permissionItems[0].content = <div>{permissions["Administrator"]}</div>
      this.props.contentOrganizerStore.permissionItems[1].content = <div>{permissions["Editor"]}</div>
      this.props.contentOrganizerStore.permissionItems[2].content = <div>{permissions["Reader"]}</div>
    } else {
      this.props.contentOrganizerStore.permissionItems = [
        {
          title: new LocalizationService().strings.ContentOrganizer_PermissionsOwners,
          content: <div>{permissions["Administrator"]}</div>,
          expanded: true,
        },
        {
          title: new LocalizationService().strings.ContentOrganizer_PermissionsMembers,
          content: <div>{permissions["Editor"]}</div>,
          expanded: true,
        },
        {
          title: new LocalizationService().strings.ContentOrganizer_PermissionsReaders,
          content: <div>{permissions["Reader"]}</div>,
          expanded: true,
        }
      ];
    }
    let content = (
      <div>
        {
          <div style={{ marginTop: "10px", display: "inline-block" }}>
            <IAToggle
              label={new LocalizationService().strings.ContentOrganizer_PermissionsTargetingSetupPermissions}
              checked={this.props.contentOrganizerStore.usePermissions}
              onChange={() => {
                this.props.contentOrganizerStore.usePermissions = !this.props.contentOrganizerStore.usePermissions;
              }}
              style={{
                width: "unset",
                marginBottom: 10,
                marginRight: 2
              }}
              disabled = {!this.props.contentOrganizerStore.newSiteCollection && this.props.contentOrganizerStore.usePermissions}
              color={this.state.color}
            />
            <div style={ { position: 'initial', left: '5px', float: "left" }}>
              <IATooltip
                content="Tooltip"
                type={"bubble"}
                styles={{
                  width: 300,
                  color: "#999999",
                  textAlign: "center",
                  marginBottom: "5px"
                }}
                componentContent={
                  <div>
                    <ul>
                      <li>{this.props.contentOrganizerStore.newSiteCollection
                      ? new LocalizationService().strings.ContentOrganizer_EnablePermissionsInfo
                      : new LocalizationService().strings.ContentOrganizer_RequiredPermissionsInfo}</li>
                    </ul>
                  </div>
                }>
                <IAIcon
                  title="Info"
                  size={17}
                  color={this.state.color}
                  style={{
                    float: "left"
                  }} />
              </IATooltip>
            </div>
            {this.props.contentOrganizerStore.usePermissions && !this.props.contentOrganizerStore.isExistingContentOrganizerInstance &&
              <div>
                <IAToggle
                  label={new LocalizationService().strings.ContentOrganizer_PermissionsInheritContentOrganizerAppPermissions}
                  checked={this.props.contentOrganizerStore.inheritAppPermissions}
                  onChange={() => {
                    this.props.contentOrganizerStore.inheritAppPermissions = !this.props.contentOrganizerStore.inheritAppPermissions;
                    this.togglePermissionsFromSource(ContentOrganizerPermissionsInheritanceSource.Application, this.props.contentOrganizerStore.inheritAppPermissions);
                  }}
                  style={{
                    width: "unset",
                    marginBottom: 10,
                    marginRight: 2
                  }}
                  color={this.state.color}
                />
                {!this.props.contentOrganizerStore.newSiteCollection &&
                  <IAToggle
                    label={new LocalizationService().strings.ContentOrganizer_PermissionsInheritSharePointSitePermissions}
                    checked={this.props.contentOrganizerStore.inheritSitePermissions}
                    onChange={() => {
                      this.props.contentOrganizerStore.inheritSitePermissions = !this.props.contentOrganizerStore.inheritSitePermissions;
                      this.togglePermissionsFromSource(ContentOrganizerPermissionsInheritanceSource.Site, this.props.contentOrganizerStore.inheritSitePermissions);
                    }}
                    style={{
                      width: "unset",
                      marginBottom: 10,
                      marginRight: 2
                    }}
                    color={this.state.color}
                  />
                }
              </div>
            }
          </div>
        }
        {this.props.contentOrganizerStore.usePermissions &&
          <div style={{ display: "block" }}>
            <div>
              {
                <div>
                  <PeopleSearch
                    token={this.props.contentOrganizerStore.token}
                    tenant={this.props.contentOrganizerStore.tenant}
                    environment={this.props.contentOrganizerStore.environment}
                    placeholder={new LocalizationService().strings.Permissions_AddPlaceholder}
                    siteName={"Intraactive-Messages"}
                    onSelect={(user: ISharePointUser) => {
                      this.props.contentOrganizerStore.userToAdd = {
                        user: Helper.syncUserProperties({
                          displayName: user.title,
                          email: user.email,
                          userName: user.loginName,
                          userPrincipalName: user.userPrincipalName,
                        }),
                        role: "Reader",
                        source: [ContentOrganizerPermissionsInheritanceSource.Custom]
                      };
                    }}
                  />
                  {this.props.contentOrganizerStore.userToAdd &&
                    <div>
                      <IAPersonaSmall
                        profileImage={Helper.getProfileOrGroupImage(this.props.contentOrganizerStore.environment, this.props.contentOrganizerStore.tenant, this.props.contentOrganizerStore.token, this.props.contentOrganizerStore.userToAdd.user.email, this.props.contentOrganizerStore.userToAdd.user.userName)}
                        name={this.props.contentOrganizerStore.userToAdd.user.title}
                        title={Helper.getProfileOrGroupType(this.localizationService, this.props.contentOrganizerStore.userToAdd.user.userName)}
                        metadata={
                          <PermissionSwitch
                            selectedOption={{
                              title: this.getPermissionsTitleFromKey(this.props.contentOrganizerStore.userToAdd.role),
                              key: this.props.contentOrganizerStore.userToAdd.role
                            }}
                            selectOption={(selectedOption: "Administrator" | "Editor" | "Reader" | "Remove") => {
                              if (selectedOption === "Remove") {
                                this.props.contentOrganizerStore.userToAdd = undefined;
                              } else {
                                this.props.contentOrganizerStore.userToAdd.role = selectedOption;
                              }
                            }}
                          />
                        }
                        backgroundColor="#025159"
                        styles={{
                          width: "30%",
                          marginTop: 13,
                          clear: "both"
                        }
                        }
                        highlightColor={this.props.contentOrganizerStore.highlightColor}
                      />
                      <IAButton
                        label={new LocalizationService().strings.Permissions_AddButton}
                        style={{
                          marginTop: 13,
                          float: "left"
                        }}
                        buttonColor={this.props.contentOrganizerStore.highlightColor}
                        arialable={new LocalizationService().strings.Permissions_AddButton}
                        onClick={() => {
                          this.appendPermissionsToStore([this.props.contentOrganizerStore.userToAdd], ContentOrganizerPermissionsInheritanceSource.Custom);
                          this.props.contentOrganizerStore.userToAdd = undefined;
                        }}
                      />
                    </div>
                  }
                  {<div style={{ marginLeft: -8, width: "100%", height: "auto" }}>
                    <IAAccordion
                      items={this.props.contentOrganizerStore.permissionItems}
                      color="#333333"
                      backgroundColor="#ffffff"
                      collapseOthers={false}
                      style={{marginBottom: 20}}
                      onChange={(items: IAccordion[]) => {
                        this.props.contentOrganizerStore.permissionItems = items;
                        this.forceUpdate();
                      }}
                    />
                  </div>}
                </div>
              }
            </div >
          </div>
        }

      </div>);

    return (
      <div>
        {content}
      </div>
    );
  }
}
